"use client";

import React, { createContext, useState, useEffect, useMemo } from 'react';
import type { CartItem, CartContextType } from './types'; // Importer le type corrigé

// Création du contexte
export const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    try {
      const storedCart = window.localStorage.getItem('cart');
      if (storedCart) {
        setCartItems(JSON.parse(storedCart));
      }
    } catch {
      // Ignore errors
    }
    setIsLoaded(true);
  }, []);
  
  // CORRECTION : Ajouter l'état pour l'ouverture du panier
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    if (isLoaded && typeof window !== 'undefined') {
      window.localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isLoaded]);

  // Maximum total items allowed in cart
  const MAX_CART_ITEMS = 20;

  const addToCart = (productToAdd: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    setCartItems(prevItems => {
      const requestedQty = productToAdd.quantity ?? 1;
      const currentTotal = prevItems.reduce((s, it) => s + (it.quantity || 0), 0);
      if (currentTotal >= MAX_CART_ITEMS) {
        // already at limit, do nothing
        return prevItems;
      }

      const available = Math.max(0, MAX_CART_ITEMS - currentTotal);
      const qtyToAdd = Math.min(requestedQty, available);

      if (qtyToAdd <= 0) return prevItems;

      // Normalize id comparisons using strings so number/string ids are compatible
      const existingItem = prevItems.find(item => String(item.id) === String(productToAdd.id));
      if (existingItem) {
        return prevItems.map(item =>
          String(item.id) === String(productToAdd.id)
            ? { ...item, ...productToAdd, quantity: item.quantity + qtyToAdd }
            : item
        );
      }
      // Ensure stored id is a string to make downstream comparisons consistent
      const toStore = { ...productToAdd, id: String(productToAdd.id), quantity: qtyToAdd } as CartItem;
      return [...prevItems, toStore];
    });
  };

  const handleIncrease = (id: string) => {
    setCartItems((prev) => {
      const total = prev.reduce((s, it) => s + (it.quantity || 0), 0);
      if (total >= MAX_CART_ITEMS) return prev; // cannot increase

      const allowed = Math.min(1, MAX_CART_ITEMS - total);
      if (allowed <= 0) return prev;

      return prev.map((item) =>
        String(item.id) === String(id) ? { ...item, quantity: item.quantity + allowed } : item
      );
    });
  };

  const handleDecrease = (id: string) => {
    setCartItems((prev) =>
      prev.map((item) =>
        String(item.id) === String(id) && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  };

  const handleRemoveItem = (id: string) => { setCartItems(prev => prev.filter(item => String(item.id) !== String(id))); };
  const clearCart = () => { setCartItems([]); };

  // CORRECTION : Ajouter les fonctions pour contrôler l'ouverture/fermeture
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const totalPrice = useMemo(() => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cartItems]);

  // CORRECTION : L'objet 'value' doit contenir TOUTES les propriétés de CartContextType
  const value: CartContextType = {
    cartItems,
    totalPrice,
    isCartOpen, // Inclure ici
    addToCart,
    handleIncrease,
    handleDecrease,
    handleRemoveItem,
    clearCart,   // Inclure ici
    openCart,    // Inclure ici
    closeCart,   // Inclure ici
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};